import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, Loader2, Printer, Check, X, Minus, ArrowDownNarrowWide, Grid3x3 } from "lucide-react";
import DocHeader from "@/components/DocHeader";

const firstName = (n) => (n || "").split(" ")[0];
const masteryColour = (pct) => (pct >= 80 ? "#7FB069" : pct >= 50 ? "#E0B341" : "#D98A6A");

const cellStatus = (s, item) => {
  if (!s.assessed) return "na";
  return s.known.includes(item) ? "yes" : "gap";
};

function GapDot({ status }) {
  if (status === "yes") return <Dot bg="#EAF6E4" fg="#7FB069"><Check size={13} /></Dot>;
  if (status === "gap") return <Dot bg="#FBEAE4" fg="#D98A6A"><X size={13} /></Dot>;
  return <Dot bg="#F1F1EF" fg="#c4c4c4"><Minus size={13} /></Dot>;
}

export default function ClassGaps() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [lists, setLists] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortGaps, setSortGaps] = useState(true);

  useEffect(() => {
    api.get("/classes").then((r) => setCls(r.data.find((c) => c.id === classId)));
    api.get("/checklists").then((r) => {
      const presets = r.data.presets || [];
      const custom = r.data.custom || [];
      const all = [...presets, ...custom];
      setLists(all);
      const numeracy = all.find((l) => (l.category || "").toLowerCase() === "numeracy");
      setTemplateId((numeracy || all[0] || {}).id || "");
    });
  }, [classId]);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    api.get(`/classes/${classId}/checklist-gaps?template_id=${templateId}`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [templateId, classId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const assessedStudents = data.students.filter((s) => s.assessed);
    const r = data.template.items.map((item) => {
      const known = assessedStudents.filter((s) => s.known.includes(item)).length;
      const pct = assessedStudents.length ? Math.round((known / assessedStudents.length) * 100) : 0;
      return { item, pct };
    });
    return sortGaps ? [...r].sort((a, b) => a.pct - b.pct) : r;
  }, [data, sortGaps]);

  const grouped = useMemo(() => {
    const g = {};
    (lists || []).forEach((l) => { (g[l.category || "Other"] = g[l.category || "Other"] || []).push(l); });
    return g;
  }, [lists]);

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto print-area">
        <DocHeader printOnly subtitle="Class Skill Gaps" />
        <button data-testid="gaps-back-btn" onClick={() => navigate(`/class/${classId}`)}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5 no-print">
          <ChevronLeft size={16} /> Back to class
        </button>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{cls?.name || "Class"}</p>
            <h1 className="font-['Outfit'] text-4xl font-bold text-slate-800 tracking-tight mt-1 flex items-center gap-2">
              <Grid3x3 size={28} className="text-[#5d8a73]" /> Gap Analysis
            </h1>
            <p className="text-slate-400 mt-1.5">See, at a glance, which skills the whole group still needs.</p>
          </div>
          <div className="flex items-center gap-2 no-print">
            <select data-testid="gaps-list-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
              className="bg-white border border-slate-200 rounded-full px-4 py-2.5 font-semibold font-['Outfit'] text-slate-700 outline-none focus:border-[#5d8a73]">
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </optgroup>
              ))}
            </select>
            <button data-testid="gaps-sort-btn" onClick={() => setSortGaps((v) => !v)} title="Sort biggest gaps first"
              className={`rounded-full px-4 py-2.5 font-semibold font-['Outfit'] border transition flex items-center gap-2 ${sortGaps ? "bg-[#5d8a73] text-white border-[#5d8a73]" : "bg-white text-slate-700 border-slate-200"}`}>
              <ArrowDownNarrowWide size={17} /> Gaps first
            </button>
            <button data-testid="gaps-print-btn" onClick={() => window.print()}
              className="bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2.5 font-semibold font-['Outfit'] hover:border-slate-300 transition flex items-center gap-2">
              <Printer size={17} /> Print
            </button>
          </div>
        </div>

        {(loading || !data) && (
          <div className="flex justify-center py-20 text-slate-300"><Loader2 className="animate-spin" /></div>
        )}
        {!loading && data && data.students.length === 0 && (
          <p className="text-slate-400 text-center py-16">No students in this class yet.</p>
        )}
        {!loading && data && data.students.length > 0 && (
          <>
            <div className="flex items-center gap-5 mb-4 text-sm text-slate-500 no-print">
              <span className="flex items-center gap-1.5"><Dot bg="#EAF6E4" fg="#7FB069"><Check size={12} /></Dot> Has skill</span>
              <span className="flex items-center gap-1.5"><Dot bg="#FBEAE4" fg="#D98A6A"><X size={12} /></Dot> Gap</span>
              <span className="flex items-center gap-1.5"><Dot bg="#F1F1EF" fg="#b4b4b4"><Minus size={12} /></Dot> Not assessed</span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] overflow-x-auto">
              <table className="w-full border-collapse text-sm" data-testid="gaps-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left font-['Outfit'] font-bold text-slate-500 px-5 py-3 sticky left-0 bg-white z-10 min-w-[240px]">Skill</th>
                    {data.students.map((s) => (
                      <th key={s.id} title={s.name} className="px-2 py-3 font-semibold text-slate-600 text-center min-w-[64px]">{firstName(s.name)}</th>
                    ))}
                    <th className="px-4 py-3 font-['Outfit'] font-bold text-slate-500 text-right min-w-[120px]">Class</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, pct }) => (
                    <tr key={item} className="border-b border-slate-50 last:border-0 hover:bg-[#F9F9F7]/60">
                      <td className="px-5 py-2.5 text-slate-700 sticky left-0 bg-white z-10">{item}</td>
                      {data.students.map((s) => (
                        <td key={s.id} className="px-2 py-2.5 text-center">
                          <span className="inline-flex">
                            <GapDot status={cellStatus(s, item)} />
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="h-2 w-16 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: masteryColour(pct) }} />
                          </div>
                          <span className="font-bold w-9 text-right" style={{ color: masteryColour(pct) }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              “Class” shows the percentage of assessed pupils who have each skill. Rows with the lowest percentage are whole-group gaps worth targeting in station teaching.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Dot({ bg, fg, children }) {
  return (
    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full" style={{ backgroundColor: bg, color: fg }}>
      {children}
    </span>
  );
}
